import { useEffect, useState } from "react";
import CustomBreadcrumb from "../common/CustomBreadcrumb";
import Alert from "../common/Alert";
import { Button, Col, Container, Row } from "react-bootstrap";
import ResponsiveTable from "../Grid/ResponsiveTable";
import InputBox from "../common/InputBox";
import {
  convertDateTimetoDateFormat,
  formatDateTime,
} from "../../helpers/date-formatter";
import { getSerialNumberDispatchReport } from "../../services/reportlog-service";
import Loader from "../common/loader";
import DatePicker from "../common/DatePicker";
import { validateAlphaNumeric } from "../../helpers/validations";

export default function SerialNumberDispatchLog() {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 1000,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [loadingMore, setLoadingMore] = useState(false);

  const [reportLogObj, setReportLogObj] = useState({
    item_code: "",
    serialNumber: "",
    validationDate: {
      fromDate: convertDateTimetoDateFormat(firstDay),
      toDate: convertDateTimetoDateFormat(today),
    },
    bpCode: "",
  });

  const handleReset = () => {
    setReportLogObj({
      item_code: "",
      serialNumber: "",
      validationDate: {
        fromDate: convertDateTimetoDateFormat(firstDay),
        toDate: convertDateTimetoDateFormat(today),
      },
      bpCode: "",
    });
    setData([]);
    setPagination({
      page: 1,
      limit: 1000,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });
  };

  const handleChange = (e, picker) => {
    const { name, value } = e?.target || {};
    if (name) {
      setReportLogObj((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }

    if (picker) {
      const fromDate = new Date(picker.startDate);
      const toDate = new Date(picker.endDate);

      // Ensure To Date is in same month/year as From Date
      if (
        fromDate.getMonth() !== toDate.getMonth() ||
        fromDate.getFullYear() !== toDate.getFullYear()
      ) {
        Alert("error", "To Date must be in the same month and year as From Date");
        return;
      }

      setReportLogObj((prevState) => ({
        ...prevState,
        validationDate: {
          fromDate: fromDate.toISOString().split("T")[0],
          toDate: toDate.toISOString().split("T")[0],
        },
      }));
    }
  };

  const shouldDisableDate = (date) => {
    const fromDate = new Date(reportLogObj.validationDate.fromDate);
    const currentDate = new Date(date);

    if (!fromDate || isNaN(fromDate)) return false;

    return (
      currentDate.getMonth() !== fromDate.getMonth() ||
      currentDate.getFullYear() !== fromDate.getFullYear()
    );
  };

  const [headers] = useState([
    {
      displayName: "Item Code",
      columnName: "item_code",
      dataType: "text",
      width: 170,
      maxLength: 50,
      displayIndication: true,
      required: true,
    },
    {
      displayName: "Serial Number",
      columnName: "serial_no",
      dataType: "text",
      width: 170,
      maxLength: 50,
      displayIndication: true,
      required: true,
    },
    {
      displayName: "To and From Date",
      columnName: "transaction_date",
      dataType: "date",
      width: 180,
      displayIndication: true,
      formatter: formatDateTime,
    },
    {
      displayName: "BP Code",
      columnName: "cust_code",
      dataType: "text",
      width: 160,
      maxLength: 50,
      displayIndication: true,
      required: true,
    },
  ]);

  useEffect(() => {
    let timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [reload]);

  const fetchPage = async (page, append = false) => {
    const isFirstPage = page === 1;
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    const payload = {
      ...reportLogObj,
      page,
      limit: 1000
    };
    
    try {
      let result = await getSerialNumberDispatchReport(payload);
      
      if (result && result.success) {
        if (append) {
          setData(prevData => [...prevData, ...result.data]);
        } else {
          setData(result.data);
        }
        setPagination(result.pagination);
        
        if (result.data.length === 0 && isFirstPage) {
          Alert("info", "No data found");
        }
      } else {
        Alert("error", "Failed to fetch dispatch logs");
      }
    } catch (error) {
      console.error('Error fetching dispatch logs:', error);
      Alert("error", error.response?.data?.message || "Error fetching dispatch logs");
    }
    
    if (isFirstPage) {
      setLoading(false);
    } else {
      setLoadingMore(false);
    }
  }

  const getLogs = async () => {
    // Reset data and fetch first page
    setData([]);
    setPagination({
      page: 1,
      limit: 1000,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });
    await fetchPage(1, false);
  };

  const loadMoreData = async () => {
    if (pagination.hasNext && !loadingMore) {
      await fetchPage(pagination.page + 1, true);
    }
  }

  const loadAllData = async () => {
    if (pagination.total > 0 && pagination.hasNext) {
      setLoadingMore(true);
      
      try {
        const remainingPages = [];
        for (let page = pagination.page + 1; page <= pagination.totalPages; page++) {
          remainingPages.push(page);
        }
        
        // Load remaining pages in batches
        const batchSize = 3;
        for (let i = 0; i < remainingPages.length; i += batchSize) {
          const batch = remainingPages.slice(i, i + batchSize);
          const promises = batch.map(page => {
            const payload = {
              ...reportLogObj,
              page,
              limit: 1000
            };
            return getSerialNumberDispatchReport(payload);
          });
          
          const results = await Promise.all(promises);
          
          results.forEach((result, index) => {
            if (result && result.success) {
              setData(prevData => [...prevData, ...result.data]);
              if (index === results.length - 1) {
                setPagination(result.pagination);
              }
            }
          });
          
          // Small delay between batches
          if (i + batchSize < remainingPages.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      } catch (error) {
        console.error('Error loading all data:', error);
        Alert("error", "Error loading additional data");
      }
      
      setLoadingMore(false);
    }
  }

  let icons = [];
  const commonProps = { onChange: handleChange };

  return (
    <>
      {loading && <Loader />}
      <CustomBreadcrumb parentMenu={"Report"} menu={"Serial Number Dispatch"} />
      <Container bsPrefix="main-body drop-down"> 
        <div className="page-title">Serial Number Dispatch</div>
        <Row>
          <Col lg={3} md={12} className="txt-input">
            <InputBox
              type="text"
              label="Item Code"
              name="item_code"
              value={reportLogObj.item_code}
              customValidation= {validateAlphaNumeric}
              {...commonProps}
            />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <InputBox
              type="text"
              label="Serial Number"
              name="serialNumber"
              value={reportLogObj.serialNumber}
              customValidation= {validateAlphaNumeric}
              {...commonProps}
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col lg={3} md={12} className="txt-input">
            <InputBox
              type="text"
              label="BP Code"
              name="bpCode"
              value={reportLogObj.bpCode}
              customValidation= {validateAlphaNumeric}
              {...commonProps}
            />
          </Col>
          <Col lg={3} md={12} className="txt-input">
            <DatePicker
              label="Validation Date"
              name="validationDate"
              value={reportLogObj.validationDate}
              onChange={handleChange}
              shouldDisableDate={shouldDisableDate}
              required={true}
            />
          </Col>
          <Col className="display-action-btn">
            <Button
              onClick={handleReset}
              className="btn btn-outline-primary reset-button"
              variant="outline-primary"
            >
              Reset
            </Button>
            <Button
              onClick={getLogs}
              className="btn btn-primary btn-color btn-go"
              disabled={loading}
            >
              Go
            </Button>
          </Col>
        </Row>
        
        {/* Pagination Controls */}
        {data.length > 0 && (
          <Row className="mb-2">
            <Col lg={12}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '5px',
                fontSize: '14px'
              }}>
                <div>
                  <strong>Records:</strong> {data.length.toLocaleString()} of {pagination.total?.toLocaleString()} total
                  {pagination.totalPages > 1 && (
                    <span className="ms-2">
                      (Page {pagination.page} of {pagination.totalPages})
                    </span>
                  )}
                </div>
                <div>
                  {pagination.hasNext && (
                    <>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={loadMoreData}
                        disabled={loadingMore}
                        className="me-2 btn-color"
                      >
                        {loadingMore ? 'Loading...' : 'Load More'}
                      </Button>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={loadAllData}
                        disabled={loadingMore}
                        className="btn-color"
                      >
                        {loadingMore ? 'Loading All...' : 'Load All'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        )}
        
        <ResponsiveTable
          headers={headers}
          data={data}
          icons={icons}
          DisplayExcelDownload={true}
          dataType={"DispatchLogs"}
          sessionName={"Serial Number Validation"}
          setReload={setReload}
          reload={reload}
          pageSize={50}
        />
      </Container>
    </>
  );
}
